// Editing a confirmed acceptance (Stage 1 slice 7). Plain module with no Vue, so it runs under
// `node --test`; it imports only other plain modules, by relative path.
//
// After confirm only POST_CONFIRM_PATHS stay editable and are saved with PATCH (save bar). Every
// other form path is frozen and changes through a correction: a group of fields saved together
// with a reason (POST /compliance/dossiers/:id/corrections).

import { FORM_PATHS, normalise, todayRome } from "./draftForm.mjs";
import { formatCivilDate } from "./deadlines.mjs";

// Same list as the API (core/compliance/dossierPatch.js POST_CONFIRM_PATHS); the API decides.
export const POST_CONFIRM_PATHS = [
  "arrival.mode",
  "arrival.transporterName",
  "arrival.incomingFir",
  "hazards.leaks",
  "hazards.fireOrFlood",
  "hazards.battery",
  "hazards.missingParts",
  "hazards.notes",
  "quarantine.decision",
];

export const HAZARD_PATHS = [
  "hazards.leaks",
  "hazards.fireOrFlood",
  "hazards.battery",
  "hazards.missingParts",
];

export function isFrozenPath(path) {
  return !POST_CONFIRM_PATHS.includes(path);
}

// Form paths that are frozen after confirm (checklist answers and reasons are frozen too).
export function frozenFormPaths() {
  return FORM_PATHS.filter(isFrozenPath);
}

// The API refuses a hazard without a quarantine decision after confirm (422 quarantine_required).
export function quarantineIncomplete(form) {
  const anyHazard = HAZARD_PATHS.some((path) => form[path] === true);
  return anyHazard && !form["quarantine.decision"];
}

// --- Groups (stage-1-slices.md D10) ------------------------------------------------------------

// group -> { prefix, fields }: `prefix` is stripped from a field path to get the request value's key.
const GROUPS = {
  "arrival.date": { prefix: "arrival.", fields: ["arrival.date"] },
  "identity.vin": { prefix: "identity.", fields: ["identity.vinRead"] },
  "identity.plate": {
    prefix: "identity.",
    fields: ["identity.plate", "identity.plateCountry"],
  },
  "identity.vehicleClass": {
    prefix: "identity.",
    fields: ["identity.vehicleClass"],
  },
  owner: { prefix: "owner.", fields: ["owner.name", "owner.taxCode"] },
  // No deliverer tax code (decided 21 Oct 2026).
  deliverer: {
    prefix: "deliverer.",
    fields: [
      "deliverer.name",
      "deliverer.idDocument.type",
      "deliverer.idDocument.number",
    ],
  },
  authorityToDeliver: {
    prefix: "authorityToDeliver.",
    fields: ["authorityToDeliver.basis", "authorityToDeliver.note"],
  },
};

export const ARRIVAL_DATE_GROUP = "arrival.date";

export function checklistGroup(code) {
  return `checklist.${code}`;
}

export function groupSpec(group) {
  if (typeof group !== "string") return null;
  if (Object.prototype.hasOwnProperty.call(GROUPS, group)) return GROUPS[group];
  const line = /^checklist\.(.+)$/.exec(group);
  if (!line) return null;
  return {
    prefix: `checklist.${line[1]}.`,
    fields: [`checklist.${line[1]}.answer`, `checklist.${line[1]}.reason`],
  };
}

export function groupFields(group) {
  const spec = groupSpec(group);
  return spec ? spec.fields : [];
}

// The nested `value` of the request from the dialog's flat form. A checklist reason is dropped
// for an answer that keeps none, like the API.
export function correctionValue(group, form) {
  const spec = groupSpec(group);
  if (!spec) return {};
  const value = {};
  spec.fields.forEach((path) => {
    const keys = path.slice(spec.prefix.length).split(".");
    let node = value;
    keys.slice(0, -1).forEach((key) => {
      if (!node[key]) node[key] = {};
      node = node[key];
    });
    node[keys[keys.length - 1]] = normalise(path, form[path]);
  });
  if (/^checklist\./.test(group)) {
    const answer = form[`${spec.prefix}answer`];
    if (answer !== "exception" && answer !== "not_applicable") {
      value.reason = null;
    }
  }
  return value;
}

// Fields of the group whose value differs from the dossier's.
export function changedFields(group, form, saved) {
  return groupFields(group).filter(
    (path) => normalise(path, form[path]) !== normalise(path, saved[path]),
  );
}

// --- Dialog fields -----------------------------------------------------------------------------

// How the dialog draws each field. label and optionKey are i18n keys; options are the API's values
// (PROVISIONAL enums, as in the API's constants).
const FIELD_SPECS = {
  "arrival.date": { type: "date", label: "acceptance.arrival.date" },
  "identity.vinRead": {
    type: "text",
    label: "acceptance.identity.vin_read",
    max: 40,
  },
  "identity.plate": {
    type: "text",
    label: "acceptance.identity.plate",
    max: 20,
  },
  "identity.plateCountry": {
    type: "country",
    label: "acceptance.identity.country",
  },
  "identity.vehicleClass": {
    type: "select",
    label: "acceptance.identity.class",
    options: ["M1", "N1"],
  },
  "owner.name": { type: "text", label: "acceptance.owner.name", max: 200 },
  "owner.taxCode": {
    type: "text",
    label: "acceptance.owner.tax_code",
    max: 32,
  },
  "deliverer.name": {
    type: "text",
    label: "acceptance.deliverer.name",
    max: 200,
  },
  "deliverer.idDocument.type": {
    type: "select",
    label: "acceptance.deliverer.id_document_type",
    options: ["identity_card", "passport", "driving_licence", "other"],
    optionKey: "acceptance.id_document_types",
  },
  "deliverer.idDocument.number": {
    type: "text",
    label: "acceptance.deliverer.id_document_number",
    max: 50,
  },
  "authorityToDeliver.basis": {
    type: "select",
    label: "acceptance.authority.label",
    options: ["is_owner", "delegation", "legal_representative", "other"],
    optionKey: "acceptance.authority_bases",
  },
  "authorityToDeliver.note": {
    type: "text",
    label: "acceptance.authority.note",
    max: 500,
  },
};

export function fieldSpec(path) {
  if (Object.prototype.hasOwnProperty.call(FIELD_SPECS, path)) {
    return FIELD_SPECS[path];
  }
  if (/^checklist\..+\.answer$/.test(path)) {
    return {
      type: "select",
      label: "acceptance.corrections.fields.answer",
      options: ["present", "exception", "not_applicable"],
      optionKey: "acceptance.checklist.answers",
    };
  }
  if (/^checklist\..+\.reason$/.test(path)) {
    return { type: "text", label: "acceptance.checklist.reason", max: 500 };
  }
  return { type: "text", label: path, max: 200 };
}

// ISO alpha-2 codes -> { value, title } with the region names of `locale`, Italy first.
export function buildCountryItems(codes, locale) {
  let names = null;
  try {
    names = new Intl.DisplayNames([locale], { type: "region" });
  } catch (err) {
    names = null;
  }
  const items = codes.map((value) => ({
    value,
    title: `${value} · ${(names && names.of(value)) || value}`,
  }));
  const italy = items.filter((item) => item.value === "IT");
  const others = items
    .filter((item) => item.value !== "IT")
    .sort((a, b) => a.title.localeCompare(b.title));
  return [...italy, ...others];
}

// --- Previous value (from the API's `corrections` map) -----------------------------------------

// How to show a stored value: { empty: true }, { key } (an i18n key) or { text }.
export function valueDisplay(path, value) {
  if (value === null || value === undefined || value === "") {
    return { empty: true };
  }
  if (path === ARRIVAL_DATE_GROUP) return { text: formatCivilDate(value) };
  const { optionKey } = fieldSpec(path);
  if (optionKey) return { key: `${optionKey}.${value}` };
  return { text: String(value) };
}

// --- History lines ----------------------------------------------------------------------------

// Fields that change after confirm without a correction, and the deadlines a correction moves:
// how History names and shows them. Correctable fields use FIELD_SPECS.
const HISTORY_FIELDS = {
  "arrival.mode": {
    label: "acceptance.arrival.mode",
    optionKey: "acceptance.arrival_modes",
  },
  "arrival.transporterName": { label: "acceptance.arrival.transporter" },
  "arrival.incomingFir": { label: "acceptance.arrival.incoming_fir" },
  "hazards.leaks": { label: "acceptance.hazards.leaks", flag: true },
  "hazards.fireOrFlood": {
    label: "acceptance.hazards.fire_or_flood",
    flag: true,
  },
  "hazards.battery": { label: "acceptance.hazards.battery", flag: true },
  "hazards.missingParts": {
    label: "acceptance.hazards.missing_parts",
    flag: true,
  },
  "hazards.notes": { label: "acceptance.hazards.notes" },
  "quarantine.decision": {
    label: "acceptance.history.sections.quarantine",
    optionKey: "acceptance.quarantine_decisions",
  },
  "deadlines.depollution": {
    label: "acceptance.deadlines.depollution",
    date: true,
  },
  "deadlines.deregistrationRequest": {
    label: "acceptance.deadlines.deregistration",
    date: true,
  },
};

// Derived by the API from the VIN as read; its change is already shown by identity.vinRead.
const HIDDEN_CHANGES = ["identity.vinNormalised"];

function historyDisplay(spec, path, value) {
  if (spec.flag) return value === true ? { text: "✓" } : { empty: true };
  if (value === null || value === undefined || value === "") {
    return { empty: true };
  }
  if (spec.date) return { text: formatCivilDate(value) };
  if (spec.optionKey) return { key: `${spec.optionKey}.${value}` };
  return valueDisplay(path, value);
}

// One event change { field, from, to } -> { label, lineCode, from, to } for a History line, or
// null when it is not shown. `label` is an i18n key; `lineCode` names the checklist line, if any;
// from / to are valueDisplay results.
export function changeLine(change) {
  const path = String((change && change.field) || "");
  if (!path || HIDDEN_CHANGES.includes(path)) return null;
  const line = /^checklist\.(.+)\.(answer|reason)$/.exec(path);
  const spec = HISTORY_FIELDS[path] || fieldSpec(path);
  return {
    label: spec.label,
    lineCode: line ? line[1] : null,
    from: historyDisplay(spec, path, change.from),
    to: historyDisplay(spec, path, change.to),
  };
}

// Corrections of the group's fields, as [{ path, from, correction }], for "Valore precedente".
export function previousValues(corrections, group) {
  const all = corrections || {};
  return groupFields(group)
    .filter((path) => all[path])
    .map((path) => ({ path, from: all[path].from, correction: all[path] }));
}

// --- Arrival date ------------------------------------------------------------------------------

// The Europe/Rome civil day the acceptance was confirmed: the latest arrival date allowed.
export function confirmDay(confirmedAt) {
  if (!confirmedAt) return null;
  const date = new Date(confirmedAt);
  return Number.isNaN(date.getTime()) ? null : todayRome(date);
}

export function isLater(oldDate, newDate) {
  return Boolean(oldDate && newDate && newDate > oldDate);
}

// A date the preview can ask about: well formed and not after the confirm day.
export function previewableDate(date, maxDay) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(String(date || "")) &&
    (!maxDay || String(date) <= maxDay)
  );
}

// dryRun `deadlinesPreview` -> [{ kind, from, to }] in DD/MM/YYYY; [] when there is none.
export function previewRows(preview) {
  if (!preview) return [];
  return ["depollution", "deregistrationRequest"]
    .filter((kind) => preview[kind])
    .map((kind) => ({
      kind,
      from: formatCivilDate(preview[kind].from),
      to: formatCivilDate(preview[kind].to),
    }));
}
